import React, { useState, useEffect, useCallback, useRef } from "react";
import {
  Dialog,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import MapContainer from "./MapContainer";
import SidePanel from "./SidePanel";
import RouteOptions from "./RouteOptions";
import WaypointsList from "./WaypointsList";
import AuthModal from "./AuthModal";
import RouteEmailConfig from "./RouteEmailConfig";
import MyRoutesPanel from "./MyRoutesPanel";
import CollapsibleSection from "./CollapsibleSection";
import TrafficSection from "./sections/TrafficSection";
import IncidentsSection from "./sections/IncidentsSection";
import ConstructionSection from "./sections/ConstructionSection";
import WeatherSection from "./sections/WeatherSection";
import CameraSection from "./sections/CameraSection";
import { formatDuration, CONGESTION_STYLES } from "./sections/formatters";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  MapPin,
  Activity,
  AlertTriangle,
  Construction,
  CloudAlert,
  Camera,
  User,
} from "lucide-react";
import useCustomRouting from "@/hooks/useCustomRouting";
import useTrafficOverlay from "@/hooks/useTrafficOverlay";
import useMapLayers from "@/hooks/useMapLayers";
import useSectionCollapse from "@/hooks/useSectionCollapse";
import { useAuth } from "@/context/AuthContext";
import { register } from "@/services/authService";
import {
  saveRouteAlertConfig,
  toAlertPreferencesDto,
} from "@/services/routeAlertService";
import {
  getAllRoutes,
  getRoute,
  updateRoute,
  deleteRoute,
  getRouteCameras,
  getRouteTraffic,
} from "@/services/routeService";

const RoutePlanner = ({ initialView }) => {
  const {
    user,
    isLoggedIn,
    isLoading: authLoading,
    login: authLogin,
  } = useAuth();

  // State for auth and alerts
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [serverFieldErrors, setServerFieldErrors] = useState({});
  const [enrollStatus, setEnrollStatus] = useState(null);

  // Route state to fix button enablement
  const [calculatedRoutes, setCalculatedRoutes] = useState([]);

  // Saved routes state
  const [savedRoutes, setSavedRoutes] = useState([]);
  const [editingRouteId, setEditingRouteId] = useState(null);
  const [featuredCamera, setFeaturedCamera] = useState(null);
  const [isDirty, setIsDirty] = useState(false);
  const [discardConfirm, setDiscardConfirm] = useState(null); // { action: () => void }
  const savedRouteDataRef = useRef(null);
  const autoLoadedRef = useRef(false);
  const loginForEnrollRef = useRef(false);

  // Auth and email config modals — open immediately if initialView is "login" or "register"
  const [showAuthModal, setShowAuthModal] = useState(
    initialView === "login" || initialView === "register",
  );
  const [initialAuthTab] = useState(
    initialView === "register" ? "register" : "login",
  );
  const [showEmailConfigModal, setShowEmailConfigModal] = useState(false);
  const [alertsConfigTarget, setAlertsConfigTarget] = useState(null); // {routeId, initialConfig} for editing existing alerts

  // Use our custom routing hook instead of Google Maps
  const {
    mapRef,
    directionsService,
    directionsRenderer,
    startInputRef,
    endInputRef,
    startAutocompleteRef,
    endAutocompleteRef,
    isDraggable,
    setIsDraggable,
    startLocation,
    endLocation,
    start,
    setStart,
    end,
    setEnd,
    waypoints,
    removeWaypoint,
    clearWaypoints,
    clearRoute,
    captureActiveRoutePath,
    isCalculating: isRouteCalculating,
    routeError,
    lastRouteResponse,
    loadSavedRoute,
    loadSavedRouteFromServer,
    refreshTraffic,
    trafficLinks,
    map,
    showEventMarkers,
    clearEventMarkers,
    showCameraMarkers,
    clearCameraMarkers,
    setLayerVisibility,
    createLayerControl,
    showLocationMarkers,
    hideLocationMarkers,
  } = useCustomRouting();

  const { layers, toggleLayer } = useMapLayers();
  const layersRef = useRef(layers);
  useEffect(() => {
    layersRef.current = layers;
  }, [layers]);

  const { sections, toggleSection, setSections } = useSectionCollapse();

  // Smart default: collapse Route Options when viewing a saved route
  const smartDefaultAppliedRef = useRef(false);
  useEffect(() => {
    if (smartDefaultAppliedRef.current) return;
    if (editingRouteId) {
      smartDefaultAppliedRef.current = true;
      setSections((prev) => ({ ...prev, routeOptions: false }));
    }
  }, [editingRouteId, setSections]);

  // Show/hide A & B markers based on Route Options section expanded state.
  // Include startLocation/endLocation so we re-hide after setLocationMarkers recreates markers.
  useEffect(() => {
    if (sections.routeOptions) {
      showLocationMarkers();
    } else {
      hideLocationMarkers();
    }
  }, [
    sections.routeOptions,
    startLocation,
    endLocation,
    showLocationMarkers,
    hideLocationMarkers,
  ]);

  // Traffic congestion overlay on route
  const { showTrafficOverlay, clearOverlay } = useTrafficOverlay(map);

  useEffect(() => {
    if (trafficLinks?.length > 0) {
      showTrafficOverlay(trafficLinks);
    } else {
      clearOverlay();
    }
  }, [trafficLinks, showTrafficOverlay, clearOverlay]);

  // Add layer control to map once it's ready
  const layerControlAddedRef = useRef(false);
  useEffect(() => {
    if (!map || layerControlAddedRef.current) return;
    layerControlAddedRef.current = true;
    const control = createLayerControl(map, layers, toggleLayer);
    map.controls[window.google.maps.ControlPosition.LEFT_TOP].push(control);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [map]);

  // Sync marker visibility when layer toggles change
  useEffect(() => {
    setLayerVisibility("cameras", layers.cameras);
    setLayerVisibility("roadWork", layers.roadWork);
    setLayerVisibility("incidents", layers.incidents);
  }, [layers, setLayerVisibility]);

  // Show incident/road work markers when route response changes
  useEffect(() => {
    if (lastRouteResponse) {
      showEventMarkers(
        lastRouteResponse.incidents || [],
        lastRouteResponse.roadWork || [],
      );
      // Apply current layer visibility to newly created markers
      if (!layersRef.current.incidents) setLayerVisibility("incidents", false);
      if (!layersRef.current.roadWork) setLayerVisibility("roadWork", false);
    } else {
      clearEventMarkers();
    }
  }, [lastRouteResponse, showEventMarkers, clearEventMarkers]);

  // Fetch and show camera markers when route response changes
  useEffect(() => {
    let cancelled = false;

    if (!lastRouteResponse?.path) {
      clearCameraMarkers();
      setFeaturedCamera(null);
      return;
    }

    const incidents = lastRouteResponse.incidents || [];
    const incidentDistances = incidents
      .map((inc) => inc.distanceAlongRouteMeters)
      .filter((d) => d != null);

    // Find worst congestion: lowest speed link
    let congestionDistance = null;
    const links = lastRouteResponse.links || [];
    if (links.length > 0) {
      let lowestSpeed = Infinity;
      for (const link of links) {
        if (link.speedMph != null && link.speedMph < lowestSpeed) {
          lowestSpeed = link.speedMph;
          congestionDistance = link.distanceAlongRouteMeters ?? null;
        }
      }
    }

    getRouteCameras(
      lastRouteResponse.path,
      incidentDistances,
      congestionDistance,
    )
      .then((fc) => {
        if (cancelled) return;
        const features = fc?.features || [];
        showCameraMarkers(features);
        // Apply current layer visibility to newly created markers
        if (!layersRef.current.cameras) setLayerVisibility("cameras", false);
        const featured = features.find((f) => f.properties?.featured);
        setFeaturedCamera(featured || null);
      })
      .catch((err) => {
        if (cancelled) return;
        console.error("Failed to fetch route cameras:", err);
        clearCameraMarkers();
        setFeaturedCamera(null);
      });

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lastRouteResponse]);

  // Fetch saved routes when logged in
  const fetchSavedRoutes = useCallback(async () => {
    if (!isLoggedIn) {
      setSavedRoutes([]);
      return;
    }
    try {
      const result = await getAllRoutes();
      setSavedRoutes(result?.data || []);
    } catch {
      // Silently fail — user may have just logged out
      setSavedRoutes([]);
    }
  }, [isLoggedIn]);

  useEffect(() => {
    if (!authLoading) {
      fetchSavedRoutes();
    }
  }, [authLoading, isLoggedIn, fetchSavedRoutes]);

  // Auto-load the first saved route once
  useEffect(() => {
    if (autoLoadedRef.current || savedRoutes.length === 0) return;
    autoLoadedRef.current = true;
    const firstRoute = savedRoutes[0];
    if (firstRoute.waypoints && firstRoute.waypoints.length >= 2) {
      setEditingRouteId(firstRoute.routeId);
      setIsDirty(false);
      savedRouteDataRef.current = null;
      loadSavedRouteFromServer(firstRoute.routeId, firstRoute.waypoints).then(
        (routeData) => {
          savedRouteDataRef.current = routeData
            ? JSON.stringify(routeData)
            : null;
        },
      );
    }
  }, [savedRoutes, loadSavedRouteFromServer]);

  // Track unsaved changes — mark dirty when route geometry changes during editing.
  // Compares routeData (the planned route) so that live traffic updates don't trigger dirty.
  // Baseline is set explicitly by doEditRoute/auto-load, not captured here.
  useEffect(() => {
    if (
      !editingRouteId ||
      !lastRouteResponse?.routeData ||
      !savedRouteDataRef.current
    )
      return;
    const currentRouteData = JSON.stringify(lastRouteResponse.routeData);
    if (currentRouteData !== savedRouteDataRef.current) {
      setIsDirty(true);
    }
  }, [lastRouteResponse?.routeData, editingRouteId]);

  // Auto-refresh traffic data every 60 seconds:
  // - My Routes list (congestion + travel time)
  // - Route Information card + map traffic overlay (for selected route)
  useEffect(() => {
    if (!isLoggedIn) return;
    const interval = setInterval(() => {
      fetchSavedRoutes();
      if (editingRouteId) {
        refreshTraffic(editingRouteId);
      }
    }, 60000);
    return () => clearInterval(interval);
  }, [isLoggedIn, editingRouteId, fetchSavedRoutes, refreshTraffic]);

  // Sync loading state — but don't let background route calculation
  // bleed into the email-config modal's loading/Saving indicator
  useEffect(() => {
    if (isRouteCalculating && !showEmailConfigModal) {
      setLoading(true);
    } else if (!showAuthModal && !showEmailConfigModal) {
      setLoading(false);
    }
  }, [isRouteCalculating, showAuthModal, showEmailConfigModal]);

  // Update routes when draggable route changes
  useEffect(() => {
    if (isDraggable && directionsRenderer) {
      captureActiveRoutePath();
    }
  }, [isDraggable, directionsRenderer, captureActiveRoutePath]);

  // Clear any status messages when changing routes
  useEffect(() => {
    directionsRenderer?.getRouteIndex?.();
    setError(null);
    setEnrollStatus(null);
  }, [directionsRenderer]);

  // Update calculatedRoutes whenever directions change
  useEffect(() => {
    if (directionsRenderer && directionsRenderer.getDirections()) {
      const routes = directionsRenderer.getDirections().routes;
      if (routes && routes.length > 0) {
        setCalculatedRoutes(routes);
      }
    }
  }, [directionsRenderer]);

  // Calculate routes function with waypoints support
  const calculateRoutes = () => {
    if (!directionsService || !start || !end) {
      setError("Please enter both start and destination locations");
      return;
    }

    setLoading(true);
    setError(null);
    setEnrollStatus(null);

    const origin = startLocation || start;
    const destination = endLocation || end;

    directionsService.route(
      {
        origin: origin,
        destination: destination,
        waypoints: waypoints,
        optimizeWaypoints: false,
        travelMode: "DRIVING",
        provideRouteAlternatives: true,
      },
      (response, status) => {
        if (status === "OK") {
          directionsRenderer.setDirections(response);
          directionsRenderer.setRouteIndex(0);

          if (response.routes && response.routes.length > 0) {
            setCalculatedRoutes(response.routes);
          }

          captureActiveRoutePath();
          setLoading(false);
        } else {
          // The specific failure message (e.g. out-of-coverage) is surfaced
          // via the hook's routeError, shown in the side panel
          setLoading(false);
          console.error("Directions request failed:", status);
        }
      },
    );
  };

  const selectRoute = (index) => {
    if (directionsRenderer) {
      directionsRenderer.setRouteIndex(index);
      captureActiveRoutePath();
    }
  };

  const enableRouteCustomization = () => {
    setIsDraggable(true);
    captureActiveRoutePath();
  };

  const disableRouteCustomization = () => {
    setIsDraggable(false);
  };

  const resetCustomization = () => {
    clearWaypoints();
    calculateRoutes();
  };

  // Map a SavedRouteDto to the RouteEmailConfig initialConfig shape
  const toInitialConfig = (cfg) => ({
    noAlerts: cfg.noAlerts,
    travelTime: cfg.travelTime,
    congestion: cfg.congestion,
    construction: cfg.construction,
    incident: cfg.incident,
    weather: cfg.weather,
    allSevereIncidents: cfg.allSevereIncidents,
    speedThreshold: cfg.speedThreshold,
    frequency: cfg.frequency,
    daysOfWeek: cfg.daysOfWeek,
    period: cfg.period,
    noIncidentsPeriod: cfg.noIncidentsPeriod,
  });

  // Handle "Save Route" button — skip auth modal if already logged in
  const handleEnrollInAlerts = async () => {
    if (!lastRouteResponse) {
      setError("No route selected to enroll for alerts");
      return;
    }

    setError(null);
    setEnrollStatus(null);
    setAlertsConfigTarget(null);

    if (isLoggedIn) {
      // Updating a saved route: preload its stored alert config so the dialog
      // shows the saved settings instead of defaults, which the save would
      // then silently write back over the real ones (gateway#10)
      if (editingRouteId) {
        try {
          const response = await getRoute(editingRouteId);
          if (response?.data) {
            setAlertsConfigTarget({
              initialConfig: toInitialConfig(response.data),
            });
          }
        } catch {
          // fall through with defaults
        }
      }
      setShowEmailConfigModal(true);
    } else {
      loginForEnrollRef.current = true;
      setShowAuthModal(true);
    }
  };

  // Handle login submission
  const handleLogin = async (loginData) => {
    try {
      setLoading(true);
      setError(null);

      await authLogin(loginData.email, loginData.password);

      setLoading(false);
      setShowAuthModal(false);

      // Show the email configuration modal only if login was triggered from save/enroll flow
      if (loginForEnrollRef.current) {
        loginForEnrollRef.current = false;
        setShowEmailConfigModal(true);
      }
    } catch (err) {
      setLoading(false);
      setError(err.message || "An error occurred during login.");
    }
  };

  // Handle registration submission
  const handleRegister = async (registerData) => {
    try {
      setLoading(true);
      setError(null);
      setServerFieldErrors({});

      const result = await register({
        email: registerData.email,
        password: registerData.password,
      });

      setLoading(false);
      setShowAuthModal(false);
      setEnrollStatus(
        result.message ||
          "Registration successful. Check your email to verify, then log in to set up alerts.",
      );
    } catch (err) {
      setLoading(false);
      setError(err.message || "An error occurred during registration.");
      setServerFieldErrors(err.fieldErrors || {});
    }
  };

  // Build waypoints array from current route state, including labels from input text
  const buildWaypointsWithLabels = () => {
    const routeWaypoints = [];
    if (startLocation) {
      routeWaypoints.push({
        lat: startLocation.lat,
        lng: startLocation.lng,
        label: start || undefined,
      });
    }
    waypoints.forEach((wp) => {
      const loc = wp.location || wp;
      routeWaypoints.push({ lat: loc.lat, lng: loc.lng });
    });
    if (endLocation) {
      routeWaypoints.push({
        lat: endLocation.lat,
        lng: endLocation.lng,
        label: end || undefined,
      });
    }
    return routeWaypoints;
  };

  // Handle saving email configuration (create new or update existing)
  const handleSaveEmailConfig = async (configData) => {
    try {
      setLoading(true);
      setError(null);

      const routeWaypoints = buildWaypointsWithLabels();

      if (alertsConfigTarget?.routeId) {
        // Update existing route's preferences
        await updateRoute(alertsConfigTarget.routeId, {
          preferences: toAlertPreferencesDto(configData),
        });
      } else if (editingRouteId) {
        // Updating route geometry + preferences
        await saveRouteAlertConfig({
          displayName: `${start} to ${end}`,
          waypoints: routeWaypoints,
          routeData: lastRouteResponse?.routeData || null,
          alertConfig: configData,
          routeId: editingRouteId,
        });
      } else {
        // Create new route
        await saveRouteAlertConfig({
          displayName: `${start} to ${end}`,
          waypoints: routeWaypoints,
          routeData: lastRouteResponse?.routeData || null,
          alertConfig: configData,
        });
      }

      setLoading(false);
      setShowEmailConfigModal(false);
      setAlertsConfigTarget(null);
      setEditingRouteId(null);
      setEnrollStatus(
        alertsConfigTarget?.routeId
          ? "Alert settings updated!"
          : "Email alerts configured successfully!",
      );
      fetchSavedRoutes();
    } catch (err) {
      setLoading(false);
      setError(
        err.message ||
          "An error occurred while saving your alert configuration.",
      );
      setShowEmailConfigModal(false);
    }
  };

  // My Routes panel handlers
  const doCreateNewRoute = useCallback(() => {
    setEditingRouteId(null);
    setIsDirty(false);
    savedRouteDataRef.current = null;
    setStart("");
    setEnd("");
    clearRoute();
    setEnrollStatus(null);
    setError(null);
    setSections((prev) => ({ ...prev, routeOptions: true }));
  }, [setStart, setEnd, clearRoute, setSections]);

  const handleCreateNewRoute = useCallback(() => {
    if (isDirty) {
      setDiscardConfirm({ action: doCreateNewRoute });
      return;
    }
    doCreateNewRoute();
  }, [isDirty, doCreateNewRoute]);

  const doEditRoute = useCallback(
    async (routeId) => {
      const route = savedRoutes.find((r) => r.routeId === routeId);
      if (!route?.waypoints || route.waypoints.length < 2) return;
      try {
        setLoading(true);
        setEditingRouteId(routeId);
        setIsDirty(false);
        savedRouteDataRef.current = null;
        const routeData = await loadSavedRouteFromServer(
          routeId,
          route.waypoints,
        );
        savedRouteDataRef.current = routeData
          ? JSON.stringify(routeData)
          : null;
        setLoading(false);
      } catch (err) {
        setLoading(false);
        setError("Failed to load route for editing");
      }
    },
    [savedRoutes, loadSavedRouteFromServer],
  );

  const handleEditRoute = async (routeId) => {
    if (routeId === editingRouteId) return; // already editing this route
    if (isDirty) {
      setDiscardConfirm({ action: () => doEditRoute(routeId) });
      return;
    }
    doEditRoute(routeId);
  };

  const handleRenameRoute = async (routeId, newName) => {
    try {
      await updateRoute(routeId, { displayName: newName });
      fetchSavedRoutes();
    } catch {
      setError("Failed to rename route");
    }
  };

  const handleDeleteRoute = async (routeId) => {
    try {
      await deleteRoute(routeId);
      if (editingRouteId === routeId) {
        setEditingRouteId(null);
      }
      fetchSavedRoutes();
    } catch {
      setError("Failed to delete route");
    }
  };

  const handleConfigureAlerts = async (routeId) => {
    const route = savedRoutes.find((r) => r.routeId === routeId);
    if (!route) return;
    // The routes.json list is a slim summary without the alert config, so
    // fetch the route's full SavedRouteDto — building initialConfig from the
    // list item left the dialog showing another route's values (gateway#10)
    let cfg = route;
    try {
      const response = await getRoute(routeId);
      cfg = response?.data || route;
    } catch {
      // fall back to the list item; the dialog will show defaults
    }
    setAlertsConfigTarget({ routeId, initialConfig: toInitialConfig(cfg) });
    setShowEmailConfigModal(true);
  };

  // Handle "Update Route" — save geometry changes to an existing route
  const handleUpdateRoute = async () => {
    if (!editingRouteId || !lastRouteResponse) return;

    try {
      setLoading(true);
      setError(null);
      const routeWaypoints = buildWaypointsWithLabels();
      await updateRoute(editingRouteId, {
        displayName: `${start} to ${end}`,
        waypoints: routeWaypoints,
        routeData: lastRouteResponse.routeData || null,
      });
      setLoading(false);
      setIsDirty(false);
      savedRouteDataRef.current = lastRouteResponse?.routeData
        ? JSON.stringify(lastRouteResponse.routeData)
        : null;
      setEnrollStatus("Route updated!");
      fetchSavedRoutes();
    } catch (err) {
      setLoading(false);
      setError("Failed to update route");
    }
  };

  return (
    <div className="flex flex-col md:flex-row w-full h-full">
      <div className="relative flex-1 min-h-0 order-first md:order-last">
        <MapContainer mapRef={mapRef} isCalculating={isRouteCalculating} />
      </div>
      <SidePanel defaultExpanded={!isLoggedIn}>
        <div>
          {/* My Routes panel — displayed first */}
          {isLoggedIn ? (
            <>
              <MyRoutesPanel
                routes={savedRoutes}
                onEditRoute={handleEditRoute}
                onRenameRoute={handleRenameRoute}
                onDeleteRoute={handleDeleteRoute}
                onConfigureAlerts={handleConfigureAlerts}
                onCreateNew={handleCreateNewRoute}
                editingRouteId={editingRouteId}
                loading={loading}
              />
              <a
                href="/Routes/MyAccount"
                className="flex items-center gap-1.5 px-3 py-1.5 text-xs text-gray-500 hover:text-blue-600 transition-colors no-underline"
              >
                <User className="w-3.5 h-3.5" />
                My Account
              </a>
            </>
          ) : (
            <Card className="p-3">
              <h3 className="text-base font-semibold">My Routes</h3>
              <p className="text-xs opacity-60 mt-1">
                Log in to see your saved routes.
              </p>
              <Button
                size="sm"
                variant="outline"
                className="mt-2"
                onClick={() => setShowAuthModal(true)}
              >
                Log In / Register
              </Button>
            </Card>
          )}

          <hr
            style={{
              border: "none",
              borderTop: "2px solid #9ca3af",
              margin: 0,
            }}
          />
          <CollapsibleSection
            title="Route Options"
            icon={MapPin}
            expanded={sections.routeOptions}
            onToggle={() => toggleSection("routeOptions")}
          >
            <RouteOptions
              start={start}
              setStart={setStart}
              end={end}
              setEnd={setEnd}
              startInputRef={startInputRef}
              endInputRef={endInputRef}
              startAutocompleteRef={startAutocompleteRef}
              endAutocompleteRef={endAutocompleteRef}
              enableRouteCustomization={enableRouteCustomization}
              disableRouteCustomization={disableRouteCustomization}
              resetCustomization={resetCustomization}
              handleEnrollInAlerts={
                editingRouteId ? handleUpdateRoute : handleEnrollInAlerts
              }
              routes={calculatedRoutes}
              loading={loading}
              error={error || routeError}
              enrollStatus={enrollStatus}
              isDraggable={isDraggable}
              hasRoute={calculatedRoutes.length > 0 || !!lastRouteResponse}
              enrollLabel={editingRouteId ? "Update Route" : undefined}
              enrollDisabled={editingRouteId ? !isDirty : false}
              editingRouteId={editingRouteId}
              onConfigureAlerts={() =>
                editingRouteId && handleConfigureAlerts(editingRouteId)
              }
            />
          </CollapsibleSection>

          {lastRouteResponse && (
            <>
              <hr
                style={{
                  border: "none",
                  borderTop: "2px solid #9ca3af",
                  margin: 0,
                }}
              />
              <CollapsibleSection
                title="Traffic"
                icon={Activity}
                expanded={sections.traffic}
                onToggle={() => toggleSection("traffic")}
                badge={
                  <span className="text-xs font-medium text-gray-500 dark:text-gray-400">
                    {formatDuration(lastRouteResponse.travelTimeSeconds)}
                    {lastRouteResponse.congestionLevel && (
                      <span
                        className="ml-1.5 inline-block w-2 h-2 rounded-full"
                        style={{
                          backgroundColor: (
                            CONGESTION_STYLES[
                              lastRouteResponse.congestionLevel
                            ] || CONGESTION_STYLES.Unknown
                          ).bgColor,
                        }}
                      />
                    )}
                  </span>
                }
              >
                <TrafficSection routeResponse={lastRouteResponse} />
              </CollapsibleSection>

              <hr
                style={{
                  border: "none",
                  borderTop: "2px solid #9ca3af",
                  margin: 0,
                }}
              />
              <CollapsibleSection
                title="Incidents"
                icon={AlertTriangle}
                expanded={sections.incidents}
                onToggle={() => toggleSection("incidents")}
                badge={
                  (lastRouteResponse.incidents?.length || 0) > 0 ? (
                    <span
                      className={`text-xs font-medium px-1.5 py-0.5 rounded ${
                        lastRouteResponse.incidents?.some(
                          (i) => i.severity === "MAJOR_EVENT_SEVERITY",
                        )
                          ? "bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400"
                          : "bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400"
                      }`}
                    >
                      {lastRouteResponse.incidents.length}
                    </span>
                  ) : null
                }
              >
                <IncidentsSection
                  incidents={lastRouteResponse.incidents || []}
                />
              </CollapsibleSection>

              <hr
                style={{
                  border: "none",
                  borderTop: "2px solid #9ca3af",
                  margin: 0,
                }}
              />
              <CollapsibleSection
                title="Construction"
                icon={Construction}
                expanded={sections.construction}
                onToggle={() => toggleSection("construction")}
                badge={
                  (lastRouteResponse.roadWork?.length || 0) > 0 ? (
                    <span className="text-xs font-medium px-1.5 py-0.5 rounded bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400">
                      {lastRouteResponse.roadWork.length}
                    </span>
                  ) : null
                }
              >
                <ConstructionSection
                  roadWork={lastRouteResponse.roadWork || []}
                />
              </CollapsibleSection>

              <hr
                style={{
                  border: "none",
                  borderTop: "2px solid #9ca3af",
                  margin: 0,
                }}
              />
              <CollapsibleSection
                title="Weather"
                icon={CloudAlert}
                expanded={sections.weather}
                onToggle={() => toggleSection("weather")}
                badge={
                  (lastRouteResponse.weatherAlerts?.length || 0) > 0 ? (
                    <span className="text-xs font-medium px-1.5 py-0.5 rounded bg-yellow-100 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-400">
                      {lastRouteResponse.weatherAlerts.length} alert
                      {lastRouteResponse.weatherAlerts.length !== 1 ? "s" : ""}
                    </span>
                  ) : null
                }
              >
                <WeatherSection
                  weatherAlerts={lastRouteResponse.weatherAlerts || []}
                  winterConditions={lastRouteResponse.winterConditions || []}
                />
              </CollapsibleSection>
            </>
          )}

          {featuredCamera && (
            <>
              <hr
                style={{
                  border: "none",
                  borderTop: "2px solid #9ca3af",
                  margin: 0,
                }}
              />
              <CollapsibleSection
                title="Camera"
                icon={Camera}
                expanded={sections.camera}
                onToggle={() => toggleSection("camera")}
                badge={
                  <span className="text-xs text-gray-500 dark:text-gray-400 truncate max-w-[120px] inline-block">
                    {featuredCamera.properties?.locDesc || ""}
                  </span>
                }
              >
                <CameraSection camera={featuredCamera} />
              </CollapsibleSection>
            </>
          )}

          {/* Show waypoints list when in customization mode */}
          {isDraggable && waypoints.length > 0 && (
            <>
              <hr
                style={{
                  border: "none",
                  borderTop: "2px solid #9ca3af",
                  margin: 0,
                }}
              />
              <WaypointsList
                waypoints={waypoints}
                removeWaypoint={removeWaypoint}
              />
            </>
          )}
        </div>
      </SidePanel>

      {/* Auth Modal */}
      <AuthModal
        isOpen={showAuthModal}
        onClose={() => setShowAuthModal(false)}
        onLogin={handleLogin}
        onRegister={handleRegister}
        loading={loading}
        error={error}
        serverFieldErrors={serverFieldErrors}
        initialTab={initialAuthTab}
      />

      {/* Unsaved changes confirmation dialog */}
      <Dialog isOpen={!!discardConfirm} onClose={() => setDiscardConfirm(null)}>
        <DialogTitle>Unsaved Changes</DialogTitle>
        <DialogDescription>
          You have unsaved changes to this route. Discard changes?
        </DialogDescription>
        <DialogFooter>
          <Button variant="outline" onClick={() => setDiscardConfirm(null)}>
            Cancel
          </Button>
          <Button
            variant="destructive"
            className="bg-red-600 hover:bg-red-700 text-white"
            onClick={() => {
              const action = discardConfirm?.action;
              setDiscardConfirm(null);
              setIsDirty(false);
              if (action) action();
            }}
          >
            Discard
          </Button>
        </DialogFooter>
      </Dialog>

      {/* Route Email Configuration Modal */}
      <RouteEmailConfig
        isOpen={showEmailConfigModal}
        onClose={() => {
          setShowEmailConfigModal(false);
          setAlertsConfigTarget(null);
        }}
        onSaveConfig={handleSaveEmailConfig}
        userData={user}
        loading={loading}
        error={error}
        initialConfig={alertsConfigTarget?.initialConfig}
        saveLabel={
          alertsConfigTarget?.routeId ? "Update Alert Settings" : undefined
        }
        routeId={alertsConfigTarget?.routeId}
      />
    </div>
  );
};

export default RoutePlanner;
